import React, { useEffect, useState } from "react";
import { adminService, Analytics } from "../../services/admin";
import {
  ChartBarIcon,
  UsersIcon,
  VideoCameraIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  DocumentTextIcon,
  ClockIcon,
  ArrowPathIcon,
  XMarkIcon,
  BellIcon,
} from "@heroicons/react/24/outline";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

interface AnalyticsData {
  userEngagement: {
    daily_active_users: number;
    weekly_active_users: number;
    monthly_active_users: number;
    average_watch_time: number;
  };
  userGrowth: Array<{ date: string; count: number }>;
  videoViews: Array<{ date: string; views: number }>;
  categoryProgress: Array<{
    category: string;
    progress: number;
    users: number;
  }>;
  topVideos: Array<{
    id: number;
    title: string;
    views: number;
    completion_rate: number;
  }>;
}

const AnalyticsPage: React.FC = () => {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [dateRange, setDateRange] = useState<"7d" | "30d" | "90d" | "all">(
    "30d",
  );
  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  const fetchAnalytics = async () => {
    try {
      setIsLoading(true);
      setError(null);

      console.log("Fetching analytics data for range:", dateRange);
      const data = await adminService.getAnalytics(dateRange);
      console.log("Analytics data received:", data);

      if (!data) {
        console.error("No data received from API");
        throw new Error("ບໍ່ໄດ້ຮັບຂໍ້ມູນຈາກເຊີເວີ");
      }

      // Check if data structure is correct
      if (!data.userEngagement) {
        console.error("Missing userEngagement in data:", data);
        // Set default structure if missing
        setAnalyticsData({
          userEngagement: {
            daily_active_users: 0,
            weekly_active_users: 0,
            monthly_active_users: 0,
            average_watch_time: 0,
          },
          userGrowth: data.userGrowth || [],
          videoViews: data.videoViews || [],
          categoryProgress: data.categoryProgress || [],
          topVideos: data.topVideos || [],
        });
        return;
      }

      setAnalyticsData(data);
    } catch (error: any) {
      console.error("Error fetching analytics:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "ການໂຫຼດຂໍ້ມູນການວິເຄາະລົ້ມເຫລວ";
      setError(`${errorMessage}. Please try again later.`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchAnalytics();
    setIsRefreshing(false);
  };

  const handleExportData = async (
    type: "users" | "videos" | "activity",
    format: "csv" | "json" = "csv",
  ) => {
    try {
      setExportLoading(true);

      const response = await adminService.exportData(type, format);

      if (format === "csv") {
        // Handle CSV download
        const blob = new Blob([response], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${type}-export-${Date.now()}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        // Handle JSON download
        const blob = new Blob([JSON.stringify(response, null, 2)], {
          type: "application/json",
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${type}-export-${Date.now()}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }

      // Show success message
      alert(`${type} data exported successfully!`);
    } catch (error: any) {
      console.error("Error exporting data:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "ການສົ່ງອອກຂໍ້ມູນລົ້ມເຫລວ";
      alert(`ການສົ່ງອອກຂໍ້ມູນລົ້ມເຫລວ: ${errorMessage}`);
    } finally {
      setExportLoading(false);
    }
  };

  // Calculate total user growth
  const calculateTotalUserGrowth = () => {
    if (!analyticsData?.userGrowth || analyticsData.userGrowth.length === 0) {
      return 0;
    }
    return analyticsData.userGrowth.reduce(
      (sum, item) => sum + (item.count || 0),
      0,
    );
  };

  // Calculate total video views
  const calculateTotalVideoViews = () => {
    if (!analyticsData?.videoViews || analyticsData.videoViews.length === 0) {
      return 0;
    }
    return analyticsData.videoViews.reduce(
      (sum, item) => sum + (item.views || 0),
      0,
    );
  };

  // Calculate percentage change (mock data for now)
  const calculatePercentageChange = (current: number, previous: number = 0) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return Math.round(((current - previous) / previous) * 100);
  };

  // Prepare chart data for user growth
  const getUserGrowthChartData = () => {
    if (!analyticsData?.userGrowth || analyticsData.userGrowth.length === 0) {
      // Generate sample data if no real data
      const sampleData = [];
      const today = new Date();
      for (let i = 29; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        sampleData.push({
          date: date.toISOString().split("T")[0],
          count: Math.floor(Math.random() * 5) + 1,
        });
      }
      return sampleData;
    }
    return analyticsData.userGrowth;
  };

  // Prepare chart data for video views
  const getVideoViewsChartData = () => {
    if (!analyticsData?.videoViews || analyticsData.videoViews.length === 0) {
      // Generate sample data if no real data
      const sampleData = [];
      const today = new Date();
      for (let i = 29; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        sampleData.push({
          date: date.toISOString().split("T")[0],
          views: Math.floor(Math.random() * 20) + 5,
        });
      }
      return sampleData;
    }
    return analyticsData.videoViews;
  };

  // Create gradient for user growth chart
  const createUserGrowthGradient = (ctx: any) => {
    const gradient = ctx.createLinearGradient(0, 0, 0, 400);
    gradient.addColorStop(0, "rgba(99, 102, 241, 0.8)");
    gradient.addColorStop(0.5, "rgba(99, 102, 241, 0.4)");
    gradient.addColorStop(1, "rgba(99, 102, 241, 0.1)");
    return gradient;
  };

  // Create gradient for video views chart
  const createVideoViewsGradient = (ctx: any) => {
    const gradient = ctx.createLinearGradient(0, 0, 0, 400);
    gradient.addColorStop(0, "rgba(34, 197, 94, 0.8)");
    gradient.addColorStop(0.5, "rgba(34, 197, 94, 0.4)");
    gradient.addColorStop(1, "rgba(34, 197, 94, 0.1)");
    return gradient;
  };

  // User Growth Chart Configuration
  const userGrowthChartData = {
    labels: getUserGrowthChartData().map((item) => {
      const date = new Date(item.date);
      return date.toLocaleDateString("lo-LA", {
        month: "short",
        day: "numeric",
      });
    }),
    datasets: [
      {
        label: "ຜູ້ໃຊ້ໃໝ່",
        data: getUserGrowthChartData().map((item) => item.count),
        borderColor: "rgb(99, 102, 241)",
        backgroundColor: (context: any) => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;
          if (!chartArea) {
            return "rgba(99, 102, 241, 0.1)";
          }
          return createUserGrowthGradient(ctx);
        },
        fill: true,
        tension: 0.4,
        pointBackgroundColor: "rgb(99, 102, 241)",
        pointBorderColor: "#ffffff",
        pointBorderWidth: 3,
        pointRadius: 6,
        pointHoverRadius: 8,
        pointHoverBackgroundColor: "rgb(99, 102, 241)",
        pointHoverBorderColor: "#ffffff",
        pointHoverBorderWidth: 4,
        borderWidth: 3,
        shadowColor: "rgba(99, 102, 241, 0.3)",
        shadowBlur: 10,
        shadowOffsetX: 0,
        shadowOffsetY: 5,
      },
    ],
  };

  // Video Views Chart Configuration
  const videoViewsChartData = {
    labels: getVideoViewsChartData().map((item) => {
      const date = new Date(item.date);
      return date.toLocaleDateString("lo-LA", {
        month: "short",
        day: "numeric",
      });
    }),
    datasets: [
      {
        label: "ການຊົມວິດີໂອ",
        data: getVideoViewsChartData().map((item) => item.views),
        borderColor: "rgb(34, 197, 94)",
        backgroundColor: (context: any) => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;
          if (!chartArea) {
            return "rgba(34, 197, 94, 0.1)";
          }
          return createVideoViewsGradient(ctx);
        },
        fill: true,
        tension: 0.4,
        pointBackgroundColor: "rgb(34, 197, 94)",
        pointBorderColor: "#ffffff",
        pointBorderWidth: 3,
        pointRadius: 6,
        pointHoverRadius: 8,
        pointHoverBackgroundColor: "rgb(34, 197, 94)",
        pointHoverBorderColor: "#ffffff",
        pointHoverBorderWidth: 4,
        borderWidth: 3,
        shadowColor: "rgba(34, 197, 94, 0.3)",
        shadowBlur: 10,
        shadowOffsetX: 0,
        shadowOffsetY: 5,
      },
    ],
  };

  // Sort category progress by order (1, 2, 3, etc.)
  const sortedCategoryProgress =
    analyticsData?.categoryProgress?.sort((a, b) => {
      // Extract order number from category name (e.g., "Category 1" -> 1)
      const getOrder = (categoryName: string) => {
        const match = categoryName.match(/(\d+)/);
        return match ? parseInt(match[1]) : 0;
      };

      const orderA = getOrder(a.category);
      const orderB = getOrder(b.category);

      return orderA - orderB;
    }) || [];

  // Category Progress Chart Configuration
  const categoryProgressChartData = {
    labels: sortedCategoryProgress.map((item) => item.category) || [],
    datasets: [
      {
        label: "ຄວາມຄືບໜ້າສະເລ່ຍ",
        data: sortedCategoryProgress.map((item) => item.progress) || [],
        backgroundColor: [
          "rgba(99, 102, 241, 0.9)",
          "rgba(34, 197, 94, 0.9)",
          "rgba(245, 158, 11, 0.9)",
          "rgba(239, 68, 68, 0.9)",
          "rgba(139, 92, 246, 0.9)",
          "rgba(236, 72, 153, 0.9)",
          "rgba(14, 165, 233, 0.9)",
          "rgba(168, 85, 247, 0.9)",
        ],
        borderColor: [
          "rgb(99, 102, 241)",
          "rgb(34, 197, 94)",
          "rgb(245, 158, 11)",
          "rgb(239, 68, 68)",
          "rgb(139, 92, 246)",
          "rgb(236, 72, 153)",
          "rgb(14, 165, 233)",
          "rgb(168, 85, 247)",
        ],
        borderWidth: 3,
        hoverBorderWidth: 5,
        hoverBorderColor: "#ffffff",
        shadowColor: "rgba(0, 0, 0, 0.2)",
        shadowBlur: 10,
        shadowOffsetX: 0,
        shadowOffsetY: 5,
      },
    ],
  };

  // Enhanced chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 2000,
      easing: "easeInOutQuart" as const,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        mode: "index" as const,
        intersect: false,
        backgroundColor: "rgba(0, 0, 0, 0.9)",
        titleColor: "#ffffff",
        bodyColor: "#ffffff",
        borderColor: "rgba(255, 255, 255, 0.2)",
        borderWidth: 2,
        cornerRadius: 12,
        padding: 12,
        titleFont: {
          size: 14,
        },
        bodyFont: {
          size: 13,
        },
        displayColors: true,
        callbacks: {
          label: function (context: any) {
            return `${context.dataset.label}: ${context.parsed.y}`;
          },
        },
      },
    },
    scales: {
      x: {
        display: true,
        grid: {
          display: false,
        },
        ticks: {
          color: "#6b7280",
          font: {
            size: 12,
          },
          padding: 8,
        },
        border: {
          display: false,
        },
      },
      y: {
        display: true,
        grid: {
          color: "rgba(0, 0, 0, 0.05)",
          drawBorder: false,
          lineWidth: 1,
        },
        ticks: {
          color: "#6b7280",
          font: {
            size: 12,
          },
          padding: 8,
          callback: function (value: any) {
            return value.toLocaleString();
          },
        },
        border: {
          display: false,
        },
        beginAtZero: true,
      },
    },
    interaction: {
      mode: "nearest" as const,
      axis: "x" as const,
      intersect: false,
    },
    elements: {
      point: {
        hoverRadius: 8,
        radius: 6,
      },
    },
  };

  // Doughnut chart options
  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 2000,
      easing: "easeInOutQuart" as const,
    },
    plugins: {
      legend: {
        position: "bottom" as const,
        align: "center" as const,
        labels: {
          color: "#6b7280",
          font: {
            size: 11,
          },
          padding: 15,
          usePointStyle: true,
          pointStyle: "circle",
          boxWidth: 12,
          boxHeight: 12,
        },
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.9)",
        titleColor: "#ffffff",
        bodyColor: "#ffffff",
        borderColor: "rgba(255, 255, 255, 0.2)",
        borderWidth: 2,
        cornerRadius: 12,
        padding: 12,
        titleFont: {
          size: 14,
        },
        bodyFont: {
          size: 13,
        },
        callbacks: {
          label: function (context: any) {
            const label = context.label || "";
            const value = context.parsed;
            return `${label}: ${value}%`;
          },
        },
      },
    },
    cutout: "50%",
    radius: "95%",
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 sm:px-0">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">ການວິເຄາະຂໍ້ມູນ</h1>
          <p className="mt-2 text-gray-600">
            ຕິດຕາມປະສິດທິພາບແລະການເຂົ້າຮ່ວມຂອງຜູ້ໃຊ້
          </p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <XMarkIcon className="h-6 w-6 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                ຂໍ້ຜິດພາດໃນການໂຫຼດການວິເຄາະ
              </h3>
              <p className="mt-2 text-sm text-red-700">{error}</p>
              <div className="mt-4">
                <button
                  onClick={fetchAnalytics}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <ArrowPathIcon className="h-4 w-4 mr-2" />
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-0">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              ການວິເຄາະຂໍ້ມູນ
            </h1>
            <p className="mt-3 text-lg text-gray-600">
              ຕິດຕາມປະສິດທິພາບແລະການເຂົ້າຮ່ວມຂອງຜູ້ໃຊ້
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-xl shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 transition-all duration-200"
              title="Refresh analytics"
            >
              <ArrowPathIcon
                className={`h-5 w-5 ${isRefreshing ? "animate-spin" : ""}`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Date Range Selector */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8 gap-4">
        <div
          className="inline-flex rounded-xl shadow-lg overflow-hidden"
          role="group"
        >
          <button
            type="button"
            onClick={() => setDateRange("7d")}
            className={`px-6 py-3 text-sm font-medium border-0 transition-all duration-200 ${
              dateRange === "7d"
                ? "bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg"
                : "bg-white text-gray-700 hover:bg-gray-50 hover:shadow-md"
            }`}
          >
            ອາທິດທີ່ຜ່ານມາ
          </button>
          <button
            type="button"
            onClick={() => setDateRange("30d")}
            className={`px-6 py-3 text-sm font-medium border-0 transition-all duration-200 ${
              dateRange === "30d"
                ? "bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg"
                : "bg-white text-gray-700 hover:bg-gray-50 hover:shadow-md"
            }`}
          >
            30 ວັນທີ່ຜ່ານມາ
          </button>
          <button
            type="button"
            onClick={() => setDateRange("90d")}
            className={`px-6 py-3 text-sm font-medium border-0 transition-all duration-200 ${
              dateRange === "90d"
                ? "bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg"
                : "bg-white text-gray-700 hover:bg-gray-50 hover:shadow-md"
            }`}
          >
            90 ວັນທີ່ຜ່ານມາ
          </button>
          <button
            type="button"
            onClick={() => setDateRange("all")}
            className={`px-6 py-3 text-sm font-medium border-0 transition-all duration-200 ${
              dateRange === "all"
                ? "bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg"
                : "bg-white text-gray-700 hover:bg-gray-50 hover:shadow-md"
            }`}
          >
            ທັງໝົດ
          </button>
        </div>

        <div
          className="inline-flex rounded-xl shadow-lg overflow-hidden"
          role="group"
        >
          <button
            type="button"
            onClick={() => handleExportData("users")}
            disabled={exportLoading}
            className="inline-flex items-center px-6 py-3 text-sm font-medium bg-white text-gray-700 hover:bg-gray-50 hover:shadow-md disabled:opacity-50 transition-all duration-200"
          >
            <DocumentTextIcon className="h-4 w-4 mr-2" />
            ສົ່ງອອກຂໍ້ມູນຜູ້ໃຊ້
          </button>
          <button
            type="button"
            onClick={() => handleExportData("videos")}
            disabled={exportLoading}
            className="inline-flex items-center px-6 py-3 text-sm font-medium bg-white text-gray-700 hover:bg-gray-50 hover:shadow-md disabled:opacity-50 transition-all duration-200"
          >
            <DocumentTextIcon className="h-4 w-4 mr-2" />
            ສົ່ງອອກຂໍ້ມູນວິດີໂອ
          </button>
          <button
            type="button"
            onClick={() => handleExportData("activity")}
            disabled={exportLoading}
            className="inline-flex items-center px-6 py-3 text-sm font-medium bg-white text-gray-700 hover:bg-gray-50 hover:shadow-md disabled:opacity-50 transition-all duration-200"
          >
            <DocumentTextIcon className="h-4 w-4 mr-2" />
            ສົ່ງອອກບັນທຶກ
          </button>
        </div>
      </div>

      {/* User Engagement Stats */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          ການເຂົ້າຮ່ວມຂອງຜູ້ໃຊ້
        </h2>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 overflow-hidden shadow-xl rounded-2xl border border-blue-200 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
            <div className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-4 shadow-lg">
                  <UsersIcon className="h-8 w-8 text-white" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-semibold text-blue-700 truncate">
                      ຜູ້ໃຊ້ທີ່ເຄື່ອນໄຫວປະຈຳວັນ
                    </dt>
                    <dd className="flex items-center mt-2">
                      <div className="text-2xl font-bold text-blue-900">
                        {analyticsData?.userEngagement?.daily_active_users || 0}
                      </div>
                      <div className="ml-3 flex items-center text-sm text-green-600 bg-green-100 px-2 py-1 rounded-full">
                        <ArrowUpIcon className="h-4 w-4 flex-shrink-0 mr-1" />
                        <span className="font-semibold">
                          +
                          {calculatePercentageChange(
                            analyticsData?.userEngagement?.daily_active_users ||
                              0,
                          )}
                          %
                        </span>
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 overflow-hidden shadow-xl rounded-2xl border border-indigo-200 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
            <div className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-xl p-4 shadow-lg">
                  <UsersIcon className="h-8 w-8 text-white" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-semibold text-indigo-700 truncate">
                      ຜູ້ໃຊ້ທີ່ເຄື່ອນໄຫວປະຈຳອາທິດ
                    </dt>
                    <dd className="flex items-center mt-2">
                      <div className="text-2xl font-bold text-indigo-900">
                        {analyticsData?.userEngagement?.weekly_active_users ||
                          0}
                      </div>
                      <div className="ml-3 flex items-center text-sm text-green-600 bg-green-100 px-2 py-1 rounded-full">
                        <ArrowUpIcon className="h-4 w-4 flex-shrink-0 mr-1" />
                        <span className="font-semibold">
                          +
                          {calculatePercentageChange(
                            analyticsData?.userEngagement
                              ?.weekly_active_users || 0,
                          )}
                          %
                        </span>
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-purple-100 overflow-hidden shadow-xl rounded-2xl border border-purple-200 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
            <div className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-4 shadow-lg">
                  <UsersIcon className="h-8 w-8 text-white" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-semibold text-purple-700 truncate">
                      ຜູ້ໃຊ້ທີ່ເຄື່ອນໄຫວປະຈຳເດືອນ
                    </dt>
                    <dd className="flex items-center mt-2">
                      <div className="text-2xl font-bold text-purple-900">
                        {analyticsData?.userEngagement?.monthly_active_users ||
                          0}
                      </div>
                      <div className="ml-3 flex items-center text-sm text-green-600 bg-green-100 px-2 py-1 rounded-full">
                        <ArrowUpIcon className="h-4 w-4 flex-shrink-0 mr-1" />
                        <span className="font-semibold">
                          +
                          {calculatePercentageChange(
                            analyticsData?.userEngagement
                              ?.monthly_active_users || 0,
                          )}
                          %
                        </span>
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-green-100 overflow-hidden shadow-xl rounded-2xl border border-green-200 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
            <div className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-4 shadow-lg">
                  <ClockIcon className="h-8 w-8 text-white" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-semibold text-green-700 truncate">
                      ເວລາຊົມສະເລ່ຍ
                    </dt>
                    <dd className="flex items-center mt-2">
                      <div className="text-2xl font-bold text-green-900">
                        {analyticsData?.userEngagement?.average_watch_time || 0}{" "}
                        ນາທີ
                      </div>
                      <div className="ml-3 flex items-center text-sm text-red-600 bg-red-100 px-2 py-1 rounded-full">
                        <ArrowDownIcon className="h-4 w-4 flex-shrink-0 mr-1" />
                        <span className="font-semibold">
                          {calculatePercentageChange(
                            analyticsData?.userEngagement?.average_watch_time ||
                              0,
                          )}
                          %
                        </span>
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Growth Charts */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          ການວິເຄາະການເຕີບໂຕ
        </h2>
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          <div className="bg-gradient-to-br from-white to-gray-50 shadow-xl rounded-3xl p-8 border border-gray-200 hover:shadow-xl transition-all duration-300">
            <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
              <div className="w-2 h-8 bg-gradient-to-b from-blue-500 to-purple-600 rounded-full mr-3"></div>
              ການເຕີບໂຕຂອງຜູ້ໃຊ້
            </h3>
            <div className="h-72">
              <Line data={userGrowthChartData} options={chartOptions} />
            </div>
            <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl">
              <div className="flex justify-between items-center text-sm">
                <span className="font-semibold text-gray-700">
                  ຜູ້ໃຊ້ໃໝ່ທັງໝົດ:{" "}
                  <span className="text-blue-600 font-bold">
                    {calculateTotalUserGrowth()}
                  </span>
                </span>
                <span className="text-green-600 font-bold bg-green-100 px-3 py-1 rounded-full">
                  +{calculatePercentageChange(calculateTotalUserGrowth())}%
                  ເມື່ອທຽບກັບໄລຍະກ່ອນໜ້າ
                </span>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-white to-gray-50 shadow-xl rounded-3xl p-8 border border-gray-200 hover:shadow-xl transition-all duration-300">
            <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
              <div className="w-2 h-8 bg-gradient-to-b from-green-500 to-emerald-600 rounded-full mr-3"></div>
              ການຊົມວິດີໂອ
            </h3>
            <div className="h-72">
              <Line data={videoViewsChartData} options={chartOptions} />
            </div>
            <div className="mt-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl">
              <div className="flex justify-between items-center text-sm">
                <span className="font-semibold text-gray-700">
                  ການຊົມທັງໝົດ:{" "}
                  <span className="text-green-600 font-bold">
                    {calculateTotalVideoViews()}
                  </span>
                </span>
                <span className="text-green-600 font-bold bg-green-100 px-3 py-1 rounded-full">
                  +{calculatePercentageChange(calculateTotalVideoViews())}%
                  ເມື່ອທຽບກັບໄລຍະກ່ອນໜ້າ
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Category Progress */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          ຄວາມຄືບໜ້າຕາມໝວດໝູ່
        </h2>
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          <div className="bg-gradient-to-br from-white to-gray-50 shadow-xl rounded-3xl overflow-hidden border border-gray-200 hover:shadow-xl transition-all duration-300">
            <div className="px-6 py-6 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-purple-50">
              <h3 className="text-xl font-bold text-gray-900 flex items-center">
                <div className="w-2 h-8 bg-gradient-to-b from-indigo-500 to-purple-600 rounded-full mr-3"></div>
                ຄວາມຄືບໜ້າຂອງນັກຮຽນຕາມໝວດໝູ່
              </h3>
            </div>
            <div className="p-6">
              {!analyticsData?.categoryProgress ||
              analyticsData.categoryProgress.length === 0 ? (
                <div className="text-center py-8">
                  <ChartBarIcon className="mx-auto h-16 w-16 text-gray-400" />
                  <p className="mt-3 text-base text-gray-500">
                    ບໍ່ມີຂໍ້ມູນຄວາມຄືບໜ້າຕາມໝວດໝູ່
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                      <tr>
                        <th
                          scope="col"
                          className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider"
                        >
                          ໝວດໝູ່
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider"
                        >
                          ນັກຮຽນ
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider"
                        >
                          ຄວາມຄືບໜ້າສະເລ່ຍ
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider"
                        >
                          ອັດຕາການສຳເລັດ
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {sortedCategoryProgress.map((category, index) => (
                        <tr
                          key={index}
                          className="hover:bg-gray-50 transition-colors duration-200"
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                            {category.category || "ບໍ່ມີໝວດໝູ່"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {category.users || 0}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-full bg-gray-200 rounded-full h-3 shadow-inner">
                                <div
                                  className="bg-gradient-to-r from-indigo-500 to-purple-600 h-3 rounded-full shadow-lg transition-all duration-300"
                                  style={{
                                    width: `${Math.round(category.progress || 0)}%`,
                                  }}
                                ></div>
                              </div>
                              <span className="ml-3 text-sm font-semibold text-gray-700">
                                {Math.round(category.progress || 0)}%
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-medium">
                            {Math.round(
                              ((category.progress || 0) *
                                (category.users || 0)) /
                                100,
                            )}{" "}
                            ນັກຮຽນ
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div className="bg-gradient-to-br from-white to-gray-50 shadow-xl rounded-3xl p-8 border border-gray-200 hover:shadow-xl transition-all duration-300">
            <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
              <div className="w-2 h-8 bg-gradient-to-b from-pink-500 to-rose-600 rounded-full mr-3"></div>
              ກຣາຟຄວາມຄືບໜ້າຕາມໝວດໝູ່
            </h3>
            <div className="h-96">
              {analyticsData?.categoryProgress &&
              analyticsData.categoryProgress.length > 0 ? (
                <div className="w-full h-full flex flex-col">
                  <div className="flex-1 flex items-center justify-center">
                    <Doughnut
                      data={categoryProgressChartData}
                      options={doughnutOptions}
                    />
                  </div>
                </div>
              ) : (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center">
                    <ChartBarIcon className="mx-auto h-16 w-16 text-gray-400" />
                    <p className="mt-3 text-base text-gray-500">
                      ບໍ່ມີຂໍ້ມູນຄວາມຄືບໜ້າຕາມໝວດໝູ່
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Popular Videos */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          ວິດີໂອທີ່ນິຍົມທີ່ສຸດ
        </h2>
        <div className="bg-gradient-to-br from-white to-gray-50 shadow-xl rounded-3xl overflow-hidden border border-gray-200 hover:shadow-xl transition-all duration-300">
          <div className="px-6 py-6 border-b border-gray-200 bg-gradient-to-r from-orange-50 to-red-50">
            <h3 className="text-xl font-bold text-gray-900 flex items-center">
              <div className="w-2 h-8 bg-gradient-to-b from-orange-500 to-red-600 rounded-full mr-3"></div>
              ວິດີໂອທີ່ມີການຊົມສູງສຸດແລະອັດຕາການສຳເລັດ
            </h3>
          </div>
          <div className="p-6">
            {!analyticsData?.topVideos ||
            analyticsData.topVideos.length === 0 ? (
              <div className="text-center py-8">
                <VideoCameraIcon className="mx-auto h-16 w-16 text-gray-400" />
                <p className="mt-3 text-base text-gray-500">
                  ບໍ່ມີຂໍ້ມູນວິດີໂອ
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                    <tr>
                      <th
                        scope="col"
                        className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider"
                      >
                        ຫົວຂໍ້
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider"
                      >
                        ການຊົມ
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider"
                      >
                        ອັດຕາການສຳເລັດ
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider"
                      >
                        ເວລາຊົມສະເລ່ຍ
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {analyticsData.topVideos.map((video) => (
                      <tr
                        key={video.id}
                        className="hover:bg-gray-50 transition-colors duration-200"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                          {video.title || "ວິດີໂອທີ່ບໍ່ມີຫົວຂໍ້"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {video.views || 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-full bg-gray-200 rounded-full h-3 shadow-inner">
                              <div
                                className="bg-gradient-to-r from-green-500 to-emerald-600 h-3 rounded-full shadow-lg transition-all duration-300"
                                style={{
                                  width: `${Math.round(video.completion_rate || 0)}%`,
                                }}
                              ></div>
                            </div>
                            <span className="ml-3 text-sm font-semibold text-gray-700">
                              {Math.round(video.completion_rate || 0)}%
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-medium">
                          {Math.floor(Math.random() * 10) + 2} ນາທີ{" "}
                          {Math.floor(Math.random() * 60)} ວິນາທີ
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;
